import {useHistory, useLocation, useRouteMatch} from "react-router-dom";
import {routes} from "../../../../../core/models/static/routes/app-routes";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {parse, stringifyUrl} from "query-string";
import useIndustry, {useCachedIndustrySchemaController} from "../../../../hooks/use-industry";
import {Box, IconButton, inputBaseClasses, MenuItem, Popover, Tooltip, Typography} from "@mui/material";
import UIUtils from "../../../../utils";
import LookupSearchViewSearchAutocomplete from "../../../../views/panel/operator/lookup-v2/search/search-autocomplete";
import {WarningOutlined} from "@mui/icons-material";
import {initialLookupV2SearchData} from "../../../../views/panel/operator/lookup-v2/search";
import useIsMounted from "../../../../hooks/use-is-mounted";
import BroadcastService from "../../../../../core/services/broadcast";
import CacheService from "../../../../../core/services/cache/cache_service";
import useSavedComponentsState from "../../../../hooks/use-saved-components-state";
import {useTranslation} from "react-i18next";
import {AppBarSearchFieldWidthBox, getAppBarSearchFieldInputPaddingSx} from "../search-mode";

/**
 * App-bar Nexus search autocomplete.
 *
 * @param {object} props
 * @param {boolean} [props.noStartActions]
 * @param {import('react').ReactNode} [props.searchModeToggler] Mode toggler as TextField endAdornment (before search icon).
 */
const PanelAppbarNexusSearchBarComponent = ({
                                                noStartActions,
                                                searchModeToggler,
                                            }) => {
    const history = useHistory();
    const historyRef = useRef(history);
    historyRef.current = history;
    const location = useLocation();
    const lookupSearchMatch = useRouteMatch(routes.lookup.search);
    const isMounted = useIsMounted();
    const lookupSearchQuery = useMemo(() => {
        if (!lookupSearchMatch)
            return null;
        return parse(location.search)
    }, [location.search, lookupSearchMatch])
    const queryRef = useRef(lookupSearchQuery);
    queryRef.current = lookupSearchQuery;

    const [featureFlags, setFeatureFlags] = useState(CacheService.getNexusSearchConfigs());
    const [advancedSearch, setAdvancedSearch] = useState(featureFlags.canToggleAdvancedMode);
    const {cachedIndustry, industries, selectIndustry, hasNoIndustries} = useIndustry({noCategories: true});
    const [industriesPopoverAnchor, setIndustriesPopoverAnchor] = useState(null);
    const [searchData, setSearchData] = useState(initialLookupV2SearchData);
    const [searchSchema, setSearchSchema] = useState({entries: []});
    const {getSchemaForIndustry} = useCachedIndustrySchemaController()
    const {highestVersion: getCachedAdvancedSearch} = useSavedComponentsState({name: "nexus_search_advanced_search", version: '1.0.0'});
    const {t} = useTranslation("components", {keyPrefix: 'panel-sections.app-bar.search-component'});
    const {t: dynamicValuesT} = useTranslation('dynamic-values');

    /**
     * As soon as the component mounts:
     * - listens to the broadcast channel for the search configs.
     */
    useEffect(() => {
        const {close: bcClose} = BroadcastService.addEventListener(BroadcastService.channelNames.nexusSearchConfigs, 'message', setFeatureFlags)
        return () => {
            bcClose();
        }
    }, [setFeatureFlags])

    /**
     * Resets the search data.
     * @type {(function(): void)|*}
     */
    const resetSearchData = useCallback(() => {
        setSearchData({...initialLookupV2SearchData, industryId: cachedIndustry?.id});
        getSchemaForIndustry(cachedIndustry?.id).then((val) => setSearchSchema(val ?? {entries: []}));
    }, [cachedIndustry?.id, getSchemaForIndustry])

    /**
     * Updates the search data when the industry changes.
     * - This is necessary because the search data is industry-specific.
     */
    useEffect(() => {
        resetSearchData();
    }, [resetSearchData]);

    /**
     * When search criteria change:
     * - Off the Nexus page → navigate with URL query (page hydrates from params).
     * - On the Nexus page → broadcast so the page applies keyword / filters without a remount.
     */
    useEffect(() => {
        const hasCriteria = searchData?.filters?.length || !!searchData?.keyword?.trim();
        if (!hasCriteria) {
            return;
        }
        const payload = {
            keyword: searchData?.keyword?.trim() || '',
            industryId: searchData?.industryId,
            filters: searchData?.filters ?? [],
        };
        if (lookupSearchMatch) {
            BroadcastService.postMessage(BroadcastService.channelNames.nexusSearchCriteria, payload);
        } else {
            historyRef.current.push(stringifyUrl({
                url: routes.lookup.search,
                query: {
                    ...queryRef.current,
                    keyword: payload.keyword || undefined,
                    industryId: payload.industryId,
                    filters: payload.filters.length ? JSON.stringify(payload.filters) : undefined,
                },
            }));
        }
        resetSearchData();
    }, [lookupSearchMatch, searchData?.filters, searchData?.industryId, searchData?.keyword, resetSearchData])

    /**
     * The styles applied to the search bar.
     *
     * @param {Theme} theme         The theme.
     * @return {ThemeMixin<Theme>}  The styles.
     */
    const mixin = useCallback((theme) => ({
        backgroundColor: theme.palette.background.paper,
        borderRadius: theme.vars?.shape.borderRadius,
        '& fieldset': {
            border: 'none',
        },
        [`& .${inputBaseClasses.root}`]: {
            ...getAppBarSearchFieldInputPaddingSx(theme),
        },
        [`& .${inputBaseClasses.input}`]: {
            fontSize: theme.typography.subtitle2.fontSize,
        }
    }), [])

    /**
     * Fetches the cached advanced search data
     * @type {(function(): Promise<void>)}
     */
    const _getCachedAdvancedSearch = useCallback(async () => {
        const cachedAdvancedSearch = await getCachedAdvancedSearch();
        if (!isMounted())
            return;
        if (cachedAdvancedSearch?.data) {
            const advancedSearch = cachedAdvancedSearch.data.value;
            BroadcastService.postMessage(BroadcastService.channelNames.nexusSearchAdvancedSearch, advancedSearch);
            setAdvancedSearch(advancedSearch);
        }
    }, [getCachedAdvancedSearch, isMounted])

    /**
     * Listens for the advanced search data changes.
     */
    useEffect(() => {
        const {close: bcClose} = BroadcastService.addEventListener(BroadcastService.channelNames.nexusSearchAdvancedSearch, 'message', setAdvancedSearch);
        return () => {
            bcClose();
        }
    }, [_getCachedAdvancedSearch]);

    /**
     * As soon as the component mounts,
     * - fetch the cached advanced search data
     */
    useEffect(() => {
        void getCachedAdvancedSearch();
    }, [getCachedAdvancedSearch]);

    return (
        <>
            <Tooltip
                title={hasNoIndustries ? t('noIndustryFound') : ""}>
                <AppBarSearchFieldWidthBox noStartActions={noStartActions}>
                    <LookupSearchViewSearchAutocomplete
                        isAdvancedMode={advancedSearch && featureFlags.canToggleAdvancedMode}
                        hideSuggestions={!featureFlags.canSeeAutocompleteSuggestions}
                        disabled={hasNoIndustries}
                        searchData={searchData}
                        setSearchData={setSearchData}
                        searchSchema={searchSchema}
                        sx={mixin}
                        TextFieldProps={{
                            fullWidth: true,
                        }}
                        InputProps={{
                            placeholder: t('inputPlaceholder'),
                        }}
                        startAdornment={
                            cachedIndustry?.id
                                ? <IconButton
                                    color={"secondary"}
                                    sx={{marginInlineEnd: 0.5, cursor: industries?.length > 1 ? 'pointer' : 'default !important'}}
                                    onClickCapture={(e) => industries?.length > 1 ? setIndustriesPopoverAnchor(e.currentTarget) : void 0}
                                >
                                    {
                                        React.cloneElement(UIUtils.getIndustryIcon(cachedIndustry?.iconFileName, null, true) ??
                                            <div/>, {
                                            style: {width: 24, height: 24},
                                        })
                                    }
                                </IconButton>
                                : <Box sx={{
                                    width: !hasNoIndustries ? 0 : 34,
                                    height: 34,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    {
                                        hasNoIndustries &&
                                        <WarningOutlined color={'warning'}/>
                                    }
                                </Box>
                        }
                        endAdornment={searchModeToggler}
                        searchIcon={false}
                    />
                </AppBarSearchFieldWidthBox>
            </Tooltip>
            <Popover
                open={!!industriesPopoverAnchor}
                onClose={() => setIndustriesPopoverAnchor(null)}
                anchorEl={industriesPopoverAnchor}
                elevation={1}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        minWidth: 200,
                        padding: 1,
                    }}
                >
                    <Typography variant={'body2'} fontWeight={600} sx={{marginBlockEnd: 1}}>
                        {t("industrySelectionTitle")}
                    </Typography>
                    {
                        industries.map((e) => (
                            <MenuItem
                                key={e.id}
                                value={e.id}
                                selected={(cachedIndustry)?.id === e.id}
                                onClick={() => {
                                    setIndustriesPopoverAnchor(null);
                                    selectIndustry(e);
                                }}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    width: 'fill-available',
                                    borderRadius: 1,
                                    marginInline: 0,
                                    padding: 1,
                                    '.fill': {
                                        color: 'primary.main',
                                    }
                                }}
                            >
                                {
                                    React.cloneElement(
                                        UIUtils.getIndustryIcon(e?.iconFileName) ?? <div/>,
                                        {
                                            style: {width: 24, height: 24, minWidth: 24},
                                        }
                                    )
                                }
                                <Typography variant={'caption'}>
                                    {dynamicValuesT($ => $.industries[e.title ?? ""] ?? "", {defaultValue: e.title ?? ""})}
                                </Typography>
                            </MenuItem>
                        ))
                    }
                </Box>
            </Popover>
        </>
    );
}

export default PanelAppbarNexusSearchBarComponent;
